/**
 * The codename family as a clickable chain under the workspace header:
 *
 *     ✉ Enquiry C001-32 · ▣ Request C001-32-1 · ▣ Request C001-32-2 (here) · ⬒ Report processing
 *
 * One chip per family member — the intake enquiry, each report request,
 * and the report as its own pill once a request has started. The current entity
 * renders as a filled, non-clickable chip. Renders nothing for
 * single-member families (a chain of one is noise).
 *
 * The enquiry page passes its own detail (children ride on it); the
 * triage page passes `enquiryId` and the strip fetches the parent once.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge, Group, Text } from '@mantine/core';
import { IconFileDescription, IconMail, IconReport } from '@tabler/icons-react';

import { vnextApiClient } from '@/services/vnext-api';
import type { EnquiryDetail } from '@/types/enquiry';
import { TriageStage, triageStageLabel } from '@/types/report-request';

interface FamilyStripProps {
  current: { type: 'enquiry' | 'report_request'; id: string };
  /** Triage page: parent enquiry id — the strip fetches its detail. */
  enquiryId?: string | null;
  /** Intake page: the already-loaded enquiry (no refetch). */
  enquiry?: EnquiryDetail | null;
}

interface FamilyNode {
  key: string;
  icon: 'intake' | 'request' | 'report';
  /** Entity-type label — icons alone weren't self-explanatory. */
  typeLabel: string;
  label: string;
  to: string;
  isCurrent: boolean;
  stage?: string;
}

const CHIP_COLOR: Record<FamilyNode['icon'], string> = {
  intake: 'gray',
  request: 'blue',
  report: 'teal',
};

function chipIcon(kind: FamilyNode['icon']) {
  switch (kind) {
    case 'intake':
      return <IconMail size={11} />;
    case 'request':
      return <IconFileDescription size={11} />;
    case 'report':
      return <IconReport size={11} />;
  }
}

export function FamilyStrip({
  current,
  enquiryId,
  enquiry: enquiryProp,
}: Readonly<FamilyStripProps>) {
  const [fetched, setFetched] = useState<EnquiryDetail | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (enquiryProp || !enquiryId) {
      setFetched(null);
      return;
    }
    void (async () => {
      try {
        const detail = await vnextApiClient.getEnquiry(enquiryId);
        if (!cancelled) {
          setFetched(detail);
        }
      } catch {
        // No strip is better than a broken one.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enquiryId, enquiryProp]);

  const enquiry = enquiryProp ?? fetched;
  if (!enquiry) {
    return null;
  }

  const nodes: FamilyNode[] = [
    {
      key: `enq-${enquiry.id}`,
      icon: 'intake',
      typeLabel: 'Enquiry',
      label: enquiry.codename ?? 'Inbound',
      to: `/admin/enquiries/${enquiry.id}`,
      isCurrent: current.type === 'enquiry' && current.id === enquiry.id,
    },
  ];
  for (const rr of enquiry.reportRequests) {
    nodes.push({
      key: `rr-${rr.id}`,
      icon: 'request',
      typeLabel: 'Request',
      label: rr.codename ?? `${rr.id.slice(0, 8)}…`,
      to: `/admin/triage/${rr.id}`,
      isCurrent: current.type === 'report_request' && current.id === rr.id,
      stage: triageStageLabel(rr.triageStage as TriageStage),
    });
    // A started request's report joins the chain as its own pill —
    // a trailing "Report →" link read as pointing at the NEXT chip.
    if (rr.triageStage === 'started' && rr.reportId) {
      nodes.push({
        key: `rep-${rr.reportId}`,
        icon: 'report',
        typeLabel: 'Report',
        label: rr.reportLifecycleStage ? rr.reportLifecycleStage.replace(/_/g, ' ') : 'view',
        to: `/reports/${rr.reportId}`,
        isCurrent: false,
      });
    }
  }

  if (nodes.length <= 1) {
    return null;
  }

  return (
    <Group gap={6} wrap="wrap" data-testid="family-strip">
      {nodes.map((node, idx) => (
        <Group key={node.key} gap={6} wrap="nowrap">
          {idx > 0 && (
            <Text size="xs" c="dimmed">
              ·
            </Text>
          )}
          {node.isCurrent ? (
            <Badge
              size="sm"
              radius="sm"
              variant="filled"
              color={CHIP_COLOR[node.icon]}
              leftSection={chipIcon(node.icon)}
              data-testid="family-node-current"
            >
              <Text component="span" size="xs" fw={400} opacity={0.75} tt="none">
                {node.typeLabel}{' '}
              </Text>
              {node.label}
              {node.stage ? ` · ${node.stage}` : ''}
            </Badge>
          ) : (
            <Badge
              size="sm"
              radius="sm"
              variant="light"
              color={CHIP_COLOR[node.icon]}
              leftSection={chipIcon(node.icon)}
              component={Link}
              to={node.to}
              style={{ cursor: 'pointer' }}
              data-testid={`family-node-${node.key}`}
            >
              <Text component="span" size="xs" fw={400} opacity={0.75} tt="none">
                {node.typeLabel}{' '}
              </Text>
              {node.label}
              {node.stage ? ` · ${node.stage}` : ''}
            </Badge>
          )}
        </Group>
      ))}
    </Group>
  );
}
